import React, {useState, useEffect, useCallback} from 'react';
import { Box, Grid2, Typography, Button, LinearProgress, Link } from '@mui/material';
import axios from 'axios';
import TarFileUpload from './components/tarFileUpload';
import FaceImageTruth from './components/faceImageTruth';
import FaceImageMatch from './components/faceImageMatch';
import FaceImageNonMatch from './components/faceImageNonMatch';
import Resumable from 'resumablejs';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer, Slide } from 'react-toastify';
import byteConversion from './components/byteConversion';
import config from './config';
import "react-toastify/dist/ReactToastify.css";
import "./toast.css"

export default function MatchingSystems({isAdmin, user, isSubmit}) {
    const serverHost = process.env.REACT_APP_BACKEND_SERVER_HOST || 'https://localhost:3001';
    const {email} = user !== undefined ? user : {};
    const [overallProgress, setOverallProgress] = useState(0);
    const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [allowed, setAllowed] = useState(true);
    const [bannerShow, setBannerShow] = useState(false);
    const [uploadType, setUploadType] = useState('');
    const [fileName, setFileName] = useState('');

    const navigate = useNavigate();

     // maps upload file keys to file objects
    const [files, setFiles] = useState({
        dockerImage: null,
        sourceOfTruthImage: null,
        matchFaceImage: null,
        nonMatchFaceImage: null,
    });

    const fileNameOverrides = {
        dockerImage: null,
        sourceOfTruthImage: 'BaseImage',
        matchFaceImage: 'MatchingImage',
        nonMatchFaceImage: 'NonMatchingImage',
    };

    const toastOptions = {
        position: "bottom-center",
        autoClose: 8000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
        transition: Slide
    }

    const fetchSubmissions = useCallback(async () => {
        try {
          const response = await axios.get(`${serverHost}/submissions/${email}`, { withCredentials: true });
          if (response.status === 200) {
              findReviewedSubmissions(response.data.filter(obj => obj.upload_type === 'matching-system'));
          }
        } catch (error) {
          console.error('Error fetching submissions:', error.response ? error.response.data : error.message);
        }
    }, [serverHost, email]);

    const allowedSubmission = useCallback(async () =>{
        const response = await axios.get(`${serverHost}/upload/${email}/matching-system/allowed`, {withCredentials: true});
        if (response.status === 200) {
            setAllowed(response.data.allowed);
        }
    }, [serverHost, email]);

    const findReviewedSubmissions = (data) => {
        for(let i = 0; i < data.length; i++) {
          if(!data[i].hasOwnProperty('approval_status')) {
            setBannerShow(true);
            setFileName(data[i].upload_key.split('/').reverse()[0]);
            setUploadType("Matching System");
          } 
        }
    }
    
    const receiveDataset = (files, type) => {
        onFileChange(files[0], type);
    }

    const onFileChange = (incomingFile, key) => {
        if(incomingFile == undefined) {
            toast.error("Error: select only ONE file", toastOptions);
            return;
        }
        const file = Object.defineProperty(incomingFile, 'key', { value: key});
        if (!file) {
            toast.error(`Error: unable to upload file "${file.name}"`, toastOptions);
            return;
        }
        if (file.name.slice(-2) === 'gz' && file.size > config.MAX_FILE_SIZE_BYTES_DOCKER) {
            toast.error(`Error: file "${file.name}" is larger than maximum allowed size`, toastOptions);
            return;
        }
        if (file.name.slice(-2) !== 'gz' && file.size > config.MAX_FILE_SIZE_BYTES_MEDIA) {
            toast.error(`Error: file "${file.name}" is larger than maximum allowed size`, toastOptions);
            return;
        }
        const validFileNameRegex = /^[a-zA-Z0-9._-]+$/;
        if (!validFileNameRegex.test(file.name)) {
            toast.error(`Error: file name "${file.name}" has invalid characters, can only contain letters, numbers, . _ or -`, toastOptions);
            return;
        }
        const fileNameExists = Object.values(files).some(f => f && f.name === file.name);
        if (fileNameExists) {
            toast.error(`Error: uploaded file name "${file.name}" must be unique, existing file ${file.name} already exists`, toastOptions);
            return;
        }
        file.uploadKey = key;
        updateFile(file);
    };

    console.log("files", files);

    const updateFile = (file) => {
        const key = file.key;
        setFiles(prevFiles => {
            const fileExists = Object.values(prevFiles).some(f => f && f.key === key);

            if(fileExists) {
                const fileKey = Object.entries(prevFiles)
                    .filter(([fileKey, f]) => f && f.key === key)
                    .map(([fileKey]) => fileKey);

                return {...prevFiles, [fileKey]: file}
            } else {
                if (key === 'dockerImage') {
                    return {...prevFiles, dockerImage: file}
                } else if (key === 'sourceOfTruthImage') {
                    return {...prevFiles, sourceOfTruthImage: file}
                } else if (key === 'matchFaceImage') {
                    return {...prevFiles, matchFaceImage: file}
                } else {
                    return {...prevFiles, nonMatchFaceImage: file}
                }
            }
        })
    }

    const resumable = new Resumable({
        target: `${config.SERVER_HOST}/upload`,
        query: { uploadType: "matching-system" }, // we will also add submission timestamp when submitted
        fileType: ['tgz', 'tar.gz', 'jpg', 'png'],
        maxFileSize: config.MAX_FILE_SIZE_BYTES_DOCKER,
        testChunks: false,
        chunkSize: config.MAX_CHUNK_SIZE_BYTES,
        withCredentials: true
    });

    resumable.on('fileAdded', function (file) {
        resumable.upload();
    });

    resumable.on('fileProgress', function (file) {
        if (!file) {
            return;
        }

        let totalProgress = 0;
        const totalFiles = resumable.files.length;

        resumable.files.forEach((file) => {
            totalProgress += file.progress();
        });

        const averageProgress = (totalProgress / totalFiles) * 100;
        setOverallProgress(averageProgress);
    });

    resumable.on('fileSuccess', function (file, message) {
        if (!file) {
            return;
        }
        toast.dismiss();
        if (resumable.files.every((file) => file.isComplete())) {
            setIsUploading(false);
            setIsSubmitted(true);
            isSubmit(false);
            navigate(isAdmin ? "/admin": "/home", {state: {formSubmitted: true}});
        }
    });

    resumable.on('fileError', function (file, message) {
        if (!file) {
            return;
        }
        toast.dismiss();
        setIsUploading(false);
        setIsSubmitEnabled(true);
        isSubmit(false);
        toast.error("Error, submission not complete, file error, please try again", toastOptions);
    });

    const onSubmit = () => {
        isSubmit(true);
        toast.dismiss();
        setIsSubmitEnabled(false);
        setIsUploading(true);
        setIsSubmitted(true);
        resumable.opts.query.submissionTimestamp = new Date().toISOString(); // add timestamp to query
        Object.values(files).forEach((file, index) => {
            resumable.addFile(file);
            if (!resumable.files[index]) {
                resumable.cancel();
                setIsSubmitEnabled(true);
                setIsUploading(false);
                setIsSubmitted(false);
                toast.dismiss();
                toast.error("Error, submission not complete, invaild file, please try again", toastOptions);
                return;
            }
            // map images to fixed file names
            if (fileNameOverrides[file.uploadKey]) {
                const fileEnding = file.type.split('/')[1];
                const useEnding = fileEnding === 'jpeg' ? 'jpg' : (fileEnding === 'jpg' ? 'jpg' : 'png');
                const fileName = `${fileNameOverrides[file.uploadKey]}.${useEnding}`;
                resumable.files[index].fileName = fileName;
            }
        });
    };
    

    useEffect(() => {
        const areAllFilesSelected = Object.keys(files).every(key => files[key] !== null);
        fetchSubmissions();
        allowedSubmission();
        setIsSubmitEnabled(!areAllFilesSelected || !allowed);
      }, [fetchSubmissions, files, allowed, allowedSubmission]);


    return(
        <Box className='container-box'>
            <Box className='banner-notice' sx={{display: bannerShow ? 'flex' : 'none'}}>
                <Typography sx={{fontSize: '20px', paddingLeft: '30px'}} color="fontTheme">
                NOTICE
                </Typography>
                <Typography color="fontTheme" sx={{fontSize: '12px', padding: '18px'}}>
                    YOUR {uploadType.toUpperCase()} SUBMISSION, "{fileName.toUpperCase()}" IS CURRENTLY UNDER REVIEW.  YOU WILL RECEIVE A SYSTEM NOTIFICATION WHEN ITS STATUS CHANGES. 
                    YOU CAN ALSO CHECK THE SUMMARY STATUS TABLE FOR UPDATES. PLEASE BE AWARE THAT YOU CANNOT SUBMIT ADDITIONAL {uploadType.toUpperCase()} ITEMS WHILE {fileName.toUpperCase()} IS BEING REVIEWED.
                </Typography>
            </Box>
            <Box sx={{marginTop: bannerShow ? '12px' : '92px'}} >
                <Typography color="fontTheme" 
                            className='page-header'>Matching System Submission</Typography>
                <Typography className='page-description' sx={{height: '96px'}}>
                    Your Matching System Docker image must implement the MdTF Matching System API.  
                    Along with your Docker image, you must submit three biometric face samples:  
                    one probe sample ("Probe Face Image"), one mated reference sample of the same identity as the probe ("Mated Reference Face Image"), 
                    and one non-mated reference sample of a different identity from the probe ("Non-mated Reference Face Image").
                </Typography>
            </Box>
            <Box className='below-content'>
               {(!isSubmitted && !isUploading) &&
                <Grid2 container columnSpacing={2}>
                    <Grid2  size={{xs: 12, lg: 3}}>
                        <Typography className='page-instruction'>
                            <b>Step 1:</b> Upload Docker Image
                        </Typography>
                    </Grid2>
                    <Grid2  size={{xs: 12, lg: 3}}>
                        <Typography className='page-instruction'>
                            <b>Step 2:</b> Upload Probe Face Image
                        </Typography>
                    </Grid2>
                    <Grid2  size={{xs: 12, lg: 3}}>
                        <Typography className='page-instruction'>
                            <b>Step 3:</b> Upload Mated Reference Face Image
                        </Typography>
                    </Grid2>
                    <Grid2  size={{xs: 12, lg: 3}}>
                        <Typography className='page-instruction'>
                            <b>Step 4:</b> Upload Non-mated Reference Face Image
                        </Typography>
                    </Grid2>
                    <Grid2 size={{xs: 12, lg: 3}}>
                            <form onSubmit={onSubmit}>
                                <TarFileUpload fileUpload={receiveDataset} bannerShow={bannerShow}/>
                            </form>
                    </Grid2>
                    <Grid2 size={{xs: 12, lg: 3}}>
                            <form onSubmit={onSubmit}>
                                <FaceImageTruth fileUpload={receiveDataset} bannerShow={bannerShow}/>
                            </form>
                    </Grid2>
                    <Grid2 size={{xs: 12, lg: 3}}>
                            <form onSubmit={onSubmit}>
                                <FaceImageMatch fileUpload={receiveDataset} bannerShow={bannerShow}/>
                            </form>
                    </Grid2>
                    <Grid2 size={{xs: 12, lg: 3}}>
                            <form onSubmit={onSubmit}>
                                <FaceImageNonMatch fileUpload={receiveDataset} bannerShow={bannerShow}/>
                            </form>
                    </Grid2>
                    <Grid2  size={{xs: 12, lg: 3}} sx={{height: '25px'}}>
                        <Typography className='file-name'>
                            {files.dockerImage !== null 
                            ? <> <b>{files.dockerImage.path.replace('./', '').toString()}</b> ({byteConversion(files.dockerImage.size)}) </> 
                            : ''}
                        </Typography>
                    </Grid2>
                    <Grid2  size={{xs: 12, lg: 3}} sx={{height: '25px'}}>
                        <Typography className='file-name'>
                            {files.sourceOfTruthImage !== null 
                            ? <> <b>{files.sourceOfTruthImage.path.replace('./', '')}</b> ({byteConversion(files.sourceOfTruthImage.size)}) </>  
                            : ''}
                       </Typography>
                    </Grid2>
                    <Grid2  size={{xs: 12, lg: 3}} sx={{height: '25px'}}>
                        <Typography className='file-name'>
                            {files.matchFaceImage !== null 
                            ? <> <b>{files.matchFaceImage.path.replace('./', '')}</b> ({byteConversion(files.matchFaceImage.size)}) </> 
                            : ''}
                        </Typography>
                    </Grid2>
                    <Grid2  size={{xs: 12, lg: 3}} sx={{height: '25px'}}>
                        <Typography className='file-name'>
                            {files.nonMatchFaceImage !== null 
                            ? <> <b>{files.nonMatchFaceImage.path.replace('./', '')}</b> ({byteConversion(files.nonMatchFaceImage.size)}) </> 
                            : ''}
                        </Typography>
                    </Grid2>
                    <Grid2  size={{xs: 12, lg: 12}} sx={{marginTop: '50px'}}>
                        <Box sx={{float: 'left'}}>
                            <Typography className='page-description'>
                                Select <b>SUBMIT</b> to upload your files.  Submission status will be communicated to your registered email address.
                            </Typography>
                        </Box>
                    </Grid2>
                    <Grid2  size={{xs: 12, lg: 12}}>
                        <Box sx={{float: 'right'}}>
                            <Button className='submit-buttons' onClick={onSubmit} disabled={isSubmitEnabled} variant='ouline'>SUBMIT</Button>
                        </Box>
                    </Grid2>
                </Grid2>
                }
                {isUploading &&
                    <>
                        <Box sx={{display: 'flex', justifyContent: 'center', height: '344px'}}>      
                            <Box sx={{width: '75%', marginRight: '10px'}}>     
                            <LinearProgress variant="determinate" 
                                            value={overallProgress}
                                            color="fontTheme"
                                            sx={{height: '30px'}}/>
                            </Box>
                            <Box>
                                <Typography color='fontTheme' variant='h6'>
                                    {parseInt(overallProgress)}%
                                </Typography>
                            </Box> 
                        </Box>
                    </>
                }
                <ToastContainer />
            </Box>
        </Box>
    )
}
